import { queryOptions } from '@tanstack/react-query'

import { API_BASE_URL } from '@/api/client'
import type { components } from '@/api/schema'

import { QuoteBuilderApiError, type QuoteBuilderApi } from '../api/client'
import { retainNewerStageRead } from '../api/queries'
import type { QuoteWorkflow } from '../api/types'

type Schemas = components['schemas']

export type FinancingState = Schemas['QbFinancingState']
export type Financing = Schemas['QbFinancingRead']
export type FinancingPreview = Schemas['QbFinancingPreview']
export type FinancingTerms = Schemas['QbFinancingTermsData']
export type FinancingTerm = FinancingTerms['term_type']
export type InvoicingState = Schemas['QbInvoicingState']
export type Invoice = Schemas['QbInvoiceRead']
export type RecordedPayment = Schemas['QbPaymentLedgerEntryRead']
export type PaymentMethod = NonNullable<RecordedPayment['method']>
export type NewPayment = Schemas['QbExternalPaymentCreate']

// Query keys follow the demo: ['quote-financing', id] and ['quote-invoicing', id].
export const financingQueryOptions = (api: QuoteBuilderApi, quoteId: string) =>
  queryOptions({
    queryKey: ['quote-financing', quoteId],
    queryFn: ({ signal }) => api.request<FinancingState>(`/quotes/${quoteId}/workflow/financing`, { signal }),
    structuralSharing: retainNewerStageRead,
  })

export const invoicingQueryKey = (quoteId: string) => ['quote-invoicing', quoteId] as const

export function financingApi(api: QuoteBuilderApi) {
  return {
    preview: (quoteId: string, terms: FinancingTerms, signal?: AbortSignal) =>
      api.request<FinancingPreview | null>(`/quotes/${quoteId}/workflow/financing/preview`, {
        method: 'POST',
        body: JSON.stringify(terms),
        signal,
      }),
    setTerms: (quoteId: string, revision: number, terms: FinancingTerms) =>
      api.request<QuoteWorkflow>(`/quotes/${quoteId}/workflow/financing`, {
        method: 'PUT',
        body: JSON.stringify({ expected_revision: revision, data: terms }),
      }),
  }
}

export function invoicingApi(api: QuoteBuilderApi) {
  return {
    read: (quoteId: string, signal?: AbortSignal) =>
      api.request<InvoicingState>(`/quotes/${quoteId}/workflow/invoicing`, { signal }),
    create: (quoteId: string, revision: number) =>
      api.request<InvoicingState>(`/quotes/${quoteId}/workflow/invoicing`, {
        method: 'POST',
        body: JSON.stringify({ expected_revision: revision }),
      }),
    createBalance: (quoteId: string, revision: number) =>
      api.request<InvoicingState>(`/quotes/${quoteId}/workflow/invoicing/balance`, {
        method: 'POST',
        body: JSON.stringify({ expected_revision: revision }),
      }),
    send: (quoteId: string, revision: number, requestId: string, invoiceSequence: 1 | 2 = 1) =>
      api.request<InvoicingState>(`/quotes/${quoteId}/workflow/invoicing/send`, {
        method: 'POST',
        body: JSON.stringify({ expected_revision: revision, request_id: requestId, invoice_sequence: invoiceSequence }),
      }),
    recordPayment: (quoteId: string, payment: NewPayment) =>
      api.request<InvoicingState>(`/quotes/${quoteId}/workflow/invoicing/payments`, {
        method: 'POST',
        body: JSON.stringify(payment),
      }),
    removePayment: (quoteId: string, paymentId: string) =>
      api.request<InvoicingState>(`/quotes/${quoteId}/workflow/invoicing/payments/${paymentId}`, { method: 'DELETE' }),
    refresh: (quoteId: string, revision: number, signal?: AbortSignal) =>
      api.request<InvoicingState>(`/quotes/${quoteId}/workflow/invoicing/refresh`, {
        method: 'POST',
        body: JSON.stringify({ expected_revision: revision }),
        signal,
      }),
  }
}

export function invoiceErrorMessage(error: unknown) {
  if (!(error instanceof QuoteBuilderApiError)) return 'Could not confirm the invoice update. Refresh before trying again.'
  if (error.status === 412) return 'The quote changed. Review the latest total and try again.'
  const messages: Record<string, string> = {
    current_final_publication_required: 'Review and publish the current final quote before invoicing.',
    invoice_publication_mismatch: 'This invoice belongs to an earlier publication. Create a new quote for revised totals.',
    quote_not_ready_for_publication: 'Complete Review for the current quote before invoicing.',
    financing_terms_required: 'Save payment terms before creating the invoice.',
    invoice_required: 'Create the first invoice before the balance invoice.',
    no_balance_to_invoice: 'These payment terms leave no balance to invoice.',
    first_invoice_void: 'The first invoice was voided. Create a new quote for revised terms.',
    invoice_snapshot_invalid: 'The published quote has incomplete invoice pricing. Review and publish a corrected final quote.',
    customer_email_required: 'Add a valid customer email before sending.',
    prepaid_terms_no_invoice: 'Prepaid terms need no invoice.',
    delivery_recipient_changed: 'The customer email changed. Review the recipient before sending.',
    stripe_unavailable: 'Invoicing is not set up in this environment. Ask your administrator.',
    stripe_mode_mismatch: 'Invoicing is set up for the wrong account here. Ask your administrator.',
    stripe_access_denied: 'The invoice service refused the request. Ask your administrator to check its access.',
    stripe_invoice_failed: 'The invoice could not be created. Try again; the same invoice will be reused.',
    stripe_invoice_send_failed: 'Delivery could not be confirmed. Wait a minute for the status to update before sending again.',
    stripe_invoice_refresh_failed: 'Payment status could not be checked just now. It is checked again every minute.',
    stripe_mark_paid_failed: 'The invoice could not be marked paid. Try again; it will not be recorded twice.',
    paid_outside_stripe: 'A payment was recorded for this quote. Record the rest the same way.',
    payment_must_match_invoice: 'Record the invoice’s full remaining amount.',
    payment_exceeds_owed: 'That is more than the customer owes on this quote.',
    invoice_pending: 'Finish creating the invoice before recording a payment.',
    payment_not_removable: 'A payment that paid an invoice cannot be removed.',
    payment_not_found: 'That payment is no longer on this quote. Refresh and try again.',
    payment_processing: 'A bank payment for this invoice is clearing. Wait for it before recording another payment.',
  }
  return messages[error.code] || 'The invoice update failed. Try again.'
}

/** An invoice PDF link: our API path for our own invoices (USA-1283), else Stripe's own PDF link. */
export function invoiceDocumentHref(url: string) {
  return url.startsWith('/') ? `${API_BASE_URL}${url}` : url
}
